/**
 * Electron Admin Dashboard
 *
 * Local admin overview showing usage metrics, system health summary,
 * backend breakdown, and quick navigation actions.
 * Only rendered on Electron — cloud uses AdminDashboard.
 *
 * @module @humanizer/studio/components/admin/ElectronAdminDashboard
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLocalAdmin } from '../../hooks/useLocalAdmin';
import type { SystemStatusResult } from '../../types/electron';

// ═══════════════════════════════════════════════════════════════════
// HELPERS
// ═══════════════════════════════════════════════════════════════════

function formatNumber(n: number): string {
  return n.toLocaleString();
}

function formatCost(millicents: number): string {
  if (millicents === 0) return 'Free';
  const dollars = millicents / 100000;
  return dollars < 0.01 ? '< $0.01' : `$${dollars.toFixed(2)}`;
}

function formatLatency(ms: number | null | undefined): string {
  if (!ms) return '-';
  return ms < 1000 ? `${ms}ms` : `${(ms / 1000).toFixed(1)}s`;
}

// ═══════════════════════════════════════════════════════════════════
// COMPONENT
// ═══════════════════════════════════════════════════════════════════

export function ElectronAdminDashboard() {
  const { getUsageSummary, getSystemStatus, getUsageByBackend } = useLocalAdmin();
  const navigate = useNavigate();
  const [summary, setSummary] = useState<any>(null);
  const [systemStatus, setSystemStatus] = useState<SystemStatusResult | null>(null);
  const [backends, setBackends] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const fetched = useRef(false);

  useEffect(() => {
    if (fetched.current) return;
    fetched.current = true;

    (async () => {
      try {
        const [s, status, b] = await Promise.all([
          getUsageSummary().catch(() => null),
          getSystemStatus().catch(() => null),
          getUsageByBackend().catch(() => []),
        ]);
        setSummary(s?.error ? null : s);
        setSystemStatus(status);
        setBackends(Array.isArray(b) ? b : []);
      } catch {
        // non-critical — show empty dashboard
      }
      setLoading(false);
    })();
  }, [getUsageSummary, getSystemStatus, getUsageByBackend]);

  if (loading) {
    return (
      <div className="admin-loading">
        <span className="admin-loading__spinner" />
      </div>
    );
  }

  const totalCalls = summary?.totalCalls ?? 0;
  const totalTokens = (summary?.totalTokensInput ?? 0) + (summary?.totalTokensOutput ?? 0);
  const estimatedCost = summary?.totalCostMillicents ?? 0;
  const avgLatency = summary?.avgLatencyMs ?? null;

  return (
    <div className="admin-dashboard">
      {/* Metric Cards */}
      <div className="admin-dashboard__grid">
        <div className="admin-metric">
          <div className="admin-metric__header">
            <span className="admin-metric__label">Total Calls</span>
            <span className="admin-metric__icon">📞</span>
          </div>
          <div className="admin-metric__value">{formatNumber(totalCalls)}</div>
          <div className="admin-metric__change admin-metric__change--neutral">
            This billing period
          </div>
        </div>

        <div className="admin-metric">
          <div className="admin-metric__header">
            <span className="admin-metric__label">Total Tokens</span>
            <span className="admin-metric__icon">🔤</span>
          </div>
          <div className="admin-metric__value">{formatNumber(totalTokens)}</div>
          <div className="admin-metric__change admin-metric__change--neutral">
            In + Out combined
          </div>
        </div>

        <div className="admin-metric">
          <div className="admin-metric__header">
            <span className="admin-metric__label">Estimated Cost</span>
            <span className="admin-metric__icon">💰</span>
          </div>
          <div className="admin-metric__value">{formatCost(estimatedCost)}</div>
          <div className="admin-metric__change admin-metric__change--neutral">
            BYOK + Cloud only
          </div>
        </div>

        <div className="admin-metric">
          <div className="admin-metric__header">
            <span className="admin-metric__label">Avg Latency</span>
            <span className="admin-metric__icon">⏱️</span>
          </div>
          <div className="admin-metric__value">{formatLatency(avgLatency)}</div>
          <div className="admin-metric__change admin-metric__change--neutral">
            Per request
          </div>
        </div>
      </div>

      {/* System Health Summary */}
      {systemStatus && (
        <div className="admin-section">
          <div className="admin-section__header">
            <h2 className="admin-section__title">System Health</h2>
          </div>
          <div className="admin-section__content">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Service</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>PostgreSQL</td>
                  <td>
                    <span className={`admin-badge admin-badge--${systemStatus.services.postgres ? 'success' : 'error'}`}>
                      {systemStatus.services.postgres ? 'Connected' : 'Disconnected'}
                    </span>
                  </td>
                </tr>
                <tr>
                  <td>Ollama</td>
                  <td>
                    <span className={`admin-badge admin-badge--${systemStatus.services.ollama ? 'success' : 'warning'}`}>
                      {systemStatus.services.ollama
                        ? `Online (${systemStatus.ollamaModelCount} models)`
                        : 'Offline'}
                    </span>
                  </td>
                </tr>
                <tr>
                  <td>Usage Tracking</td>
                  <td>
                    <span className={`admin-badge admin-badge--${systemStatus.services.usageTracking ? 'success' : 'neutral'}`}>
                      {systemStatus.services.usageTracking ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Backend Breakdown */}
      {backends.length > 0 && (
        <div className="admin-section">
          <div className="admin-section__header">
            <h2 className="admin-section__title">Backend Distribution</h2>
          </div>
          <div className="admin-section__content">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Backend</th>
                  <th>Calls</th>
                  <th>Tokens</th>
                  <th>Cost</th>
                </tr>
              </thead>
              <tbody>
                {backends.map((b: any) => (
                  <tr key={b.backend}>
                    <td>{b.backend === 'ollama' ? 'Ollama (Local)' : b.backend === 'byok' ? 'BYOK' : 'Cloud'}</td>
                    <td>{formatNumber(b.totalCalls ?? 0)}</td>
                    <td>{formatNumber((b.totalTokensInput ?? 0) + (b.totalTokensOutput ?? 0))}</td>
                    <td>{formatCost(b.totalCostMillicents ?? 0)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Quick Actions */}
      <div className="admin-section">
        <div className="admin-section__header">
          <h2 className="admin-section__title">Quick Actions</h2>
        </div>
        <div className="admin-section__content">
          <div className="admin-dashboard__grid">
            <button
              className="admin-metric"
              onClick={() => navigate('/admin/models')}
              type="button"
            >
              <div className="admin-metric__header">
                <span className="admin-metric__icon">🤖</span>
              </div>
              <div className="admin-metric__label">Configure Models</div>
            </button>
            <button
              className="admin-metric"
              onClick={() => navigate('/admin/prompts')}
              type="button"
            >
              <div className="admin-metric__header">
                <span className="admin-metric__icon">📝</span>
              </div>
              <div className="admin-metric__label">Edit Prompts</div>
            </button>
            <button
              className="admin-metric"
              onClick={() => navigate('/admin/analytics/usage')}
              type="button"
            >
              <div className="admin-metric__header">
                <span className="admin-metric__icon">📉</span>
              </div>
              <div className="admin-metric__label">View Usage</div>
            </button>
            <button
              className="admin-metric"
              onClick={() => navigate('/settings')}
              type="button"
            >
              <div className="admin-metric__header">
                <span className="admin-metric__icon">⚙️</span>
              </div>
              <div className="admin-metric__label">Settings</div>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
